/* Growth AI — dedicated organic-growth workspace inside EMRKT.CH.
 * Four blocks of value:
 *   1. Daily brief   (auto-generated, cached per UTC day)
 *   2. Chat coach    (freeform Q&A with GPT-5.2)
 *   3. Post queue    (approve, mark posted, copy captions)
 *   4. Guide viewer  (renders the ORGANIC_TRAFFIC_GUIDE.md inline)
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Sparkles, Send, RefreshCw, Copy, Check, Trash2,
  Instagram, Play, Share2, MessageCircle, Users, ExternalLink,
  BookOpen, Clock, ChevronRight,
} from "lucide-react";

import { api } from "@/lib/api";

const NETWORK_META = {
  instagram: { label: "Instagram", Icon: Instagram, color: "#E1306C" },
  tiktok: { label: "TikTok", Icon: Play, color: "#25F4EE" },
  pinterest: { label: "Pinterest", Icon: Share2, color: "#E60023" },
  facebook: { label: "Facebook", Icon: Users, color: "#4267B2" },
  linkedin: { label: "LinkedIn", Icon: Users, color: "#0A66C2" },
  x: { label: "X / Twitter", Icon: MessageCircle, color: "#ffffff" },
  reddit: { label: "Reddit", Icon: MessageCircle, color: "#FF4500" },
  whatsapp: { label: "WhatsApp", Icon: MessageCircle, color: "#25D366" },
};

export default function GrowthAIPage() {
  return (
    <div className="space-y-6" data-testid="growth-ai-page">
      <PageHeader />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <DailyBriefCard />
          <PostQueueCard />
        </div>
        <div className="space-y-6">
          <ChatCoachCard />
          <GuideCard />
        </div>
      </div>
    </div>
  );
}

/* -------------------- Header + stats -------------------- */

function PageHeader() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get(`/growth-ai/stats`)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, []);
  return (
    <div className="rounded-lg border border-white/10 bg-gradient-to-br from-[#0B1F1F] via-[#080808] to-[#080808] p-6"
         data-testid="growth-ai-header">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[color:var(--hq-cyan)]">
            <Sparkles size={12} /> Growth AI
          </div>
          <h1 className="mt-2 font-display text-2xl sm:text-3xl">
            Coach de tráfego orgânico
          </h1>
          <p className="mt-2 text-white/60 text-sm max-w-xl">
            O teu assistente diário para crescer sem gastar em anúncios.
            Recebe um plano do dia, gera posts prontos e acompanha a tua consistência.
          </p>
        </div>
        {stats && (
          <div className="grid grid-cols-3 gap-3 text-center">
            <StatCell label="posts 7d" value={stats.posted_7d} testid="stat-7d" />
            <StatCell label="posts 30d" value={stats.posted_30d} testid="stat-30d" />
            <StatCell label="em fila" value={stats.pending} testid="stat-pending" />
          </div>
        )}
      </div>
    </div>
  );
}

function StatCell({ label, value, testid }) {
  return (
    <div className="min-w-[80px] rounded border border-white/10 bg-black/40 px-3 py-2" data-testid={testid}>
      <div className="font-display text-2xl leading-none">{value ?? "—"}</div>
      <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-white/40">{label}</div>
    </div>
  );
}

/* -------------------- Daily brief -------------------- */

function DailyBriefCard() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/growth-ai/daily-brief${force ? "?force=true" : ""}`);
      setBrief(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro a carregar briefing");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  return (
    <div className="rounded-lg border border-white/10 bg-[#0a0a0a] overflow-hidden" data-testid="daily-brief-card">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="font-display text-lg">Plano de hoje</div>
          {brief?.cached && <span className="text-[9px] font-mono uppercase text-white/40 border border-white/20 px-2 py-0.5 rounded">cached</span>}
        </div>
        <button
          data-testid="brief-refresh-btn"
          disabled={loading || refreshing}
          onClick={() => { setRefreshing(true); load(true); }}
          className="text-[10px] font-mono uppercase text-white/50 hover:text-white flex items-center gap-1.5 border border-white/10 hover:border-white/30 px-3 py-1.5 rounded transition-colors"
        >
          <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
          Regenerar
        </button>
      </div>

      <div className="p-6 space-y-5">
        {loading && <div className="text-white/40 text-sm">A gerar plano do dia…</div>}
        {error && <div className="text-red-400 text-sm">{error}</div>}
        {!loading && brief && (
          <>
            <div>
              <div className="font-display text-xl leading-snug" data-testid="brief-headline">
                {brief.headline}
              </div>
              <div className="mt-1 text-white/70 text-sm" data-testid="brief-focus">
                <strong className="text-[color:var(--hq-cyan)]">Foco:</strong> {brief.focus_of_the_day}
              </div>
            </div>

            <div className="space-y-3">
              {(brief.tasks || []).map((task, i) => {
                const meta = NETWORK_META[task.network] || { label: task.network, Icon: Sparkles, color: "#22d3ee" };
                const Icon = meta.Icon;
                return (
                  <div key={i} className="flex items-start gap-3 p-3 rounded border border-white/10 bg-black/40" data-testid={`brief-task-${i}`}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                         style={{ backgroundColor: `${meta.color}20`, color: meta.color }}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono uppercase text-white/50">{meta.label}</span>
                        <span className="text-[10px] font-mono text-white/40 flex items-center gap-1">
                          <Clock size={9} /> {task.minutes}min
                        </span>
                      </div>
                      <div className="text-sm text-white/85 leading-snug">{task.action}</div>
                      {task.product_handle && (
                        <a
                          href={`/shop/${task.product_handle}`}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-[color:var(--hq-cyan)] hover:underline"
                        >
                          {task.product_handle} <ExternalLink size={9} />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {brief.tip_of_the_day && (
              <div className="p-3 rounded border-l-2 border-[color:var(--hq-cyan)] bg-[color:var(--hq-cyan)]/5" data-testid="brief-tip">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--hq-cyan)] mb-1">
                  Dica do dia
                </div>
                <div className="text-sm text-white/85">{brief.tip_of_the_day}</div>
              </div>
            )}

            {brief.streak_encouragement && (
              <div className="text-xs text-white/60 italic" data-testid="brief-streak">
                {brief.streak_encouragement}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------- Post queue + draft generator -------------------- */

function PostQueueCard() {
  const [posts, setPosts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState({
    network: "instagram", lang: "pt", count: 3, product_handle: "",
  });
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/growth-ai/queue?status=${statusFilter}`);
      setPosts(r.data.posts || []);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const generateDrafts = async () => {
    setGenerating(true);
    try {
      const payload = { ...gen };
      if (!payload.product_handle) delete payload.product_handle;
      const r = await api.post(`/growth-ai/drafts/generate`, payload);
      toast.success(`${r.data.count} posts gerados`);
      setStatusFilter("pending");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha a gerar posts");
    } finally { setGenerating(false); }
  };

  const updateStatus = async (post_id, status) => {
    try {
      await api.post(`/growth-ai/queue/${post_id}/status`, { status });
      toast.success(status === "posted" ? "Marcado como publicado ✓" : "Estado atualizado");
      load();
    } catch { toast.error("Falha a atualizar"); }
  };

  const deletePost = async (post_id) => {
    if (!window.confirm("Apagar este post da fila?")) return;
    try {
      await api.delete(`/growth-ai/queue/${post_id}`);
      load();
    } catch { toast.error("Falha a apagar"); }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-[#0a0a0a]" data-testid="post-queue-card">
      <div className="px-6 py-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg">Fila de posts</div>
          <div className="flex gap-1">
            {["pending", "posted", "skipped", "all"].map((s) => (
              <button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`text-[10px] font-mono uppercase px-2.5 py-1 rounded transition-colors ${statusFilter === s ? "bg-white/15 text-white" : "text-white/40 hover:text-white/70"}`}
              >{s}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <SelectField label="Rede" value={gen.network} onChange={(v) => setGen({ ...gen, network: v })}
                       options={Object.keys(NETWORK_META).map((k) => ({ value: k, label: NETWORK_META[k].label }))} testid="draft-network" />
          <SelectField label="Idioma" value={gen.lang} onChange={(v) => setGen({ ...gen, lang: v })}
                       options={[{value:"pt",label:"PT"},{value:"en",label:"EN"},{value:"de",label:"DE"},{value:"fr",label:"FR"},{value:"it",label:"IT"}]} testid="draft-lang" />
          <SelectField label="Quantidade" value={String(gen.count)} onChange={(v) => setGen({ ...gen, count: parseInt(v, 10) })}
                       options={[1,2,3,4,5,6].map((n) => ({ value: String(n), label: String(n) }))} testid="draft-count" />
          <div className="col-span-2">
            <label className="block text-[10px] font-mono uppercase text-white/40 mb-1">Produto (opcional)</label>
            <input
              data-testid="draft-product"
              placeholder="handle-do-produto"
              value={gen.product_handle}
              onChange={(e) => setGen({ ...gen, product_handle: e.target.value.trim() })}
              className="w-full bg-black/60 border border-white/10 rounded px-3 py-1.5 text-xs focus:border-white/40 outline-none"
            />
          </div>
        </div>
        <button
          data-testid="generate-drafts-btn"
          onClick={generateDrafts}
          disabled={generating}
          className="mt-3 w-full flex items-center justify-center gap-2 bg-[color:var(--hq-cyan)] text-black text-xs font-mono uppercase tracking-wider py-2 rounded hover:brightness-110 disabled:opacity-50 transition-all"
        >
          <Sparkles size={12} /> {generating ? "A gerar…" : "Gerar posts"}
        </button>
      </div>

      <div className="max-h-[600px] overflow-y-auto">
        {loading && <div className="p-6 text-white/40 text-sm">A carregar…</div>}
        {!loading && posts.length === 0 && (
          <div className="p-6 text-white/40 text-sm text-center">
            Nenhum post {statusFilter === "all" ? "" : `«${statusFilter}»`}. Gera acima o teu primeiro batch.
          </div>
        )}
        {posts.map((p) => (
          <PostRow key={p.post_id} post={p} onUpdate={updateStatus} onDelete={deletePost} />
        ))}
      </div>
    </div>
  );
}

function PostRow({ post, onUpdate, onDelete }) {
  const [copied, setCopied] = useState(false);
  const meta = NETWORK_META[post.network] || { label: post.network, Icon: Sparkles, color: "#22d3ee" };
  const Icon = meta.Icon;
  const fullText = [
    post.caption,
    post.cta_line,
    (post.hashtags || []).join(" "),
  ].filter(Boolean).join("\n\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("Copiado");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copiar manualmente:", fullText);
    }
  };

  return (
    <div className="px-6 py-4 border-t border-white/5" data-testid={`post-${post.post_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
             style={{ backgroundColor: `${meta.color}20`, color: meta.color }}>
          <Icon size={13} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-mono uppercase text-white/50">{meta.label}</span>
            <span className="text-[10px] font-mono uppercase text-white/40">· {post.lang}</span>
            {post.product_handle && (
              <span className="text-[10px] font-mono text-[color:var(--hq-cyan)] truncate">
                {post.product_handle}
              </span>
            )}
            <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ml-auto ${
              post.status === "posted" ? "bg-emerald-500/20 text-emerald-300" :
              post.status === "skipped" ? "bg-white/10 text-white/40" :
              "bg-amber-500/20 text-amber-300"
            }`}>{post.status}</span>
          </div>
          <div className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
            {post.caption}
          </div>
          {post.cta_line && (
            <div className="mt-2 text-xs text-white/60 italic">{post.cta_line}</div>
          )}
          {post.hashtags && post.hashtags.length > 0 && (
            <div className="mt-2 text-[11px] text-[color:var(--hq-cyan)]/80 font-mono">
              {post.hashtags.slice(0, 12).join(" ")}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <button
              data-testid={`copy-${post.post_id}`}
              onClick={copy}
              className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-white/70 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1 rounded transition-colors"
            >
              {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "copiado" : "copiar"}
            </button>
            {post.status !== "posted" && (
              <button
                data-testid={`mark-posted-${post.post_id}`}
                onClick={() => onUpdate(post.post_id, "posted")}
                className="text-[10px] font-mono uppercase text-emerald-300 hover:text-emerald-100 border border-emerald-500/30 hover:border-emerald-500 px-3 py-1 rounded transition-colors"
              >
                marcar publicado
              </button>
            )}
            {post.status === "pending" && (
              <button
                data-testid={`skip-${post.post_id}`}
                onClick={() => onUpdate(post.post_id, "skipped")}
                className="text-[10px] font-mono uppercase text-white/40 hover:text-white/70 border border-white/10 hover:border-white/30 px-3 py-1 rounded transition-colors"
              >saltar</button>
            )}
            <button
              data-testid={`delete-${post.post_id}`}
              onClick={() => onDelete(post.post_id)}
              className="ml-auto text-white/30 hover:text-red-400 transition-colors"
              aria-label="Apagar"
            ><Trash2 size={12} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase text-white/40 mb-1">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-xs focus:border-white/40 outline-none"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* -------------------- Chat coach -------------------- */

function ChatCoachCard() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Olá 👋 pergunta-me qualquer coisa sobre como crescer o teu tráfego orgânico. Exemplo: «que devo publicar hoje no Pinterest?»" },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post(`/growth-ai/chat`, { message: msg, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${e?.response?.data?.detail || "erro"}` }]);
    } finally { setSending(false); }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-[#0a0a0a] flex flex-col h-[520px]" data-testid="chat-card">
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
        <MessageCircle size={14} className="text-[color:var(--hq-cyan)]" />
        <div className="font-display text-sm">Chat com o coach</div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-snug whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-[color:var(--hq-cyan)] text-black"
                : "bg-black/50 border border-white/10 text-white/85"
            }`} data-testid={`msg-${i}`}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && <div className="text-white/40 text-xs">a pensar…</div>}
      </div>
      <div className="border-t border-white/10 p-3 flex items-center gap-2">
        <input
          data-testid="chat-input"
          placeholder="Pergunta ao coach…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          disabled={sending}
          className="flex-1 bg-black/60 border border-white/10 rounded px-3 py-2 text-sm focus:border-white/40 outline-none"
        />
        <button
          data-testid="chat-send-btn"
          onClick={send}
          disabled={sending || !input.trim()}
          className="w-9 h-9 flex items-center justify-center bg-[color:var(--hq-cyan)] text-black rounded hover:brightness-110 disabled:opacity-30 transition-all"
        ><Send size={14} /></button>
      </div>
    </div>
  );
}

/* -------------------- Guide viewer -------------------- */

function GuideCard() {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/growth-ai/guide`).then((r) => setText(r.data.markdown || "")).catch(() => {});
  }, []);

  return (
    <div className="rounded-lg border border-white/10 bg-[#0a0a0a] overflow-hidden" data-testid="guide-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-3 border-b border-white/10 flex items-center justify-between hover:bg-white/5 transition-colors"
        data-testid="guide-toggle"
      >
        <div className="flex items-center gap-2">
          <BookOpen size={14} className="text-[color:var(--hq-cyan)]" />
          <div className="font-display text-sm">Guia orgânico completo</div>
        </div>
        <ChevronRight size={14} className={`text-white/40 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="p-5 max-h-[600px] overflow-y-auto" data-testid="guide-content">
          <MarkdownRender text={text} />
        </div>
      )}
    </div>
  );
}

function MarkdownRender({ text }) {
  if (!text) return <div className="text-white/40 text-sm">Guia não disponível.</div>;
  const html = mdToHtml(text);
  return <div className="growth-ai-md" dangerouslySetInnerHTML={{ __html: html }} />;
}

function esc(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function inlineMd(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
}

function mdToHtml(md) {
  const lines = md.split("\n");
  const out = [];
  let inList = false, inTable = false, tableHeader = false;
  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  const flushTable = () => { if (inTable) { out.push("</tbody></table>"); inTable = false; tableHeader = false; } };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\|.*\|$/.test(line.trim()) && !line.match(/^\|\s*-+/)) {
      if (!inTable) { out.push('<table class="gai-table"><thead>'); inTable = true; tableHeader = true; }
      const cells = line.trim().slice(1, -1).split("|").map((c) => esc(c.trim()));
      if (tableHeader) {
        out.push("<tr>" + cells.map((c) => `<th>${inlineMd(c)}</th>`).join("") + "</tr></thead><tbody>");
        tableHeader = false;
      } else {
        out.push("<tr>" + cells.map((c) => `<td>${inlineMd(c)}</td>`).join("") + "</tr>");
      }
      continue;
    }
    if (/^\|\s*-+/.test(line)) continue;
    flushTable();

    if (/^#{1,6}\s+/.test(line)) {
      flushList();
      const level = line.match(/^#+/)[0].length;
      const content = inlineMd(esc(line.replace(/^#+\s+/, "")));
      out.push(`<h${level}>${content}</h${level}>`);
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${inlineMd(esc(line.replace(/^\s*[-*]\s+/, "")))}</li>`);
      continue;
    }
    flushList();
    if (/^>\s?/.test(line)) {
      out.push(`<blockquote>${inlineMd(esc(line.replace(/^>\s?/, "")))}</blockquote>`);
      continue;
    }
    if (line.trim() === "---") { out.push("<hr/>"); continue; }
    if (line.trim() === "") { out.push(""); continue; }
    out.push(`<p>${inlineMd(esc(line))}</p>`);
  }
  flushList();
  flushTable();
  return out.join("\n");
}
